import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowDown, Mic, Drum, RefreshCcw, Quote, Star, Loader2, Check, Mail, BatteryLow, Users2, TrendingDown } from "lucide-react";
import { CadenceFooter } from "../components/portal/CadenceFooter";
import { ChatWidget } from "../components/portal/ChatWidget";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAGE_TITLE = "Corporate Wellness Keynotes & Team Building | Casey Muze";
const PAGE_DESC =
  "Casey Muze delivers rhythm-based corporate wellness keynotes and forte-building workshops that reduce employee burnout, rebuild connection, and lift team culture. Download the Team Burnout Reset Checklist.";

const useCorporateSeo = () => {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = PAGE_TITLE;

    const setMeta = (selector, attr, content) => {
      const el = document.head.querySelector(selector);
      if (!el) return () => {};
      const prev = el.getAttribute(attr);
      el.setAttribute(attr, content);
      return () => el.setAttribute(attr, prev ?? "");
    };
    const cleanups = [
      setMeta('meta[name="description"]', "content", PAGE_DESC),
      setMeta('meta[property="og:title"]', "content", PAGE_TITLE),
      setMeta('meta[property="og:description"]', "content", PAGE_DESC),
      setMeta('meta[property="og:url"]', "content", "https://caseyscadence.info/corporate"),
      setMeta('meta[name="twitter:title"]', "content", PAGE_TITLE),
      setMeta('meta[name="twitter:description"]', "content", PAGE_DESC),
    ];

    const canonical = document.head.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical?.getAttribute("href");
    canonical?.setAttribute("href", "https://caseyscadence.info/corporate");

    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "corporate-jsonld";
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Corporate Wellness Keynotes & Forte-Building Workshops",
      provider: { "@id": "https://caseyscadence.info/#person" },
      serviceType: "Corporate team building and employee wellbeing",
      description: PAGE_DESC,
      url: "https://caseyscadence.info/corporate",
    });
    document.head.appendChild(ld);

    return () => {
      document.title = prevTitle;
      cleanups.forEach((fn) => fn());
      if (canonical && prevCanonical) canonical.setAttribute("href", prevCanonical);
      ld.remove();
    };
  }, []);
};

const PAINS = [
  { testid: "pain-burnout", Icon: BatteryLow, title: "Quiet burnout", body: "Your best people are running on empty — present on camera, absent in spirit. Burnout hides in silence until it becomes turnover." },
  { testid: "pain-silos", Icon: Users2, title: "Siloed teams", body: "Departments talk past each other. Collaboration feels forced. The culture deck says one thing; the hallway says another." },
  { testid: "pain-erosion", Icon: TrendingDown, title: "Eroding performance", body: "Silent stress quietly taxes focus, communication, and retention. By the time it shows up in metrics, it's been compounding for quarters." },
];

const FORMATS = [
  { testid: "format-keynote", Icon: Mic, title: "The Cadence Keynote", meta: "45–60 min · company-wide", body: "A high-energy, science-backed keynote that gives your whole organization a shared language for stress, rhythm, and recovery — and a felt experience of what \u201cin sync\u201d actually means." },
  { testid: "format-workshop", Icon: Drum, title: "Forte-Building Workshop", meta: "Half-day · teams up to 150", body: "Interactive bilateral-drumming sessions that turn a stressed, disconnected group into one synchronized team. No trust falls. No slide decks. Just shared rhythm that carries back into the work." },
  { testid: "format-series", Icon: RefreshCcw, title: "Wellbeing Rhythm Series", meta: "Multi-session · ongoing culture", body: "A recurring program that embeds nervous-system regulation into your culture — monthly rhythm resets, leadership cadence coaching, and tools your people keep using long after." },
];

const field =
  "w-full rounded-xl border border-[#AEE6D5]/25 bg-[#0A2E2C]/60 px-4 py-3.5 text-sm text-[#F8FAFC] placeholder-[#F8FAFC]/40 outline-none transition-colors focus:border-[#D4AF37]";

const ChecklistCapture = () => {
  const [form, setForm] = useState({ name: "", email: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | done | error
  const [errorMsg, setErrorMsg] = useState("Something went wrong — please try again in a moment.");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || status === "sending") return;
    setStatus("sending");
    try {
      const res = await fetch(`${API}/checklist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        if (data?.detail) setErrorMsg(data.detail);
        throw new Error("failed");
      }
      setStatus("done");
    } catch {
      setStatus("error");
    }
  };

  if (status === "done") {
    return (
      <div className="flex items-start gap-4 rounded-2xl border border-[#D4AF37]/40 bg-[#0A2E2C]/50 p-6" data-testid="checklist-success">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#D4AF37] text-[#0A2E2C]"><Check className="h-5 w-5" /></span>
        <div>
          <p className="font-serif text-xl text-[#F8FAFC]">Check your inbox.</p>
          <p className="mt-1 text-sm text-[#F8FAFC]/70">The Team Burnout Reset Checklist is on its way to <span className="text-[#AEE6D5]">{form.email}</span>. Twelve small moves, one shared cadence.</p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="checklist-form">
      <input className={field} data-testid="checklist-name" placeholder="Your name (optional)" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
      <input className={field} data-testid="checklist-email" type="email" placeholder="Work email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} required />
      {status === "error" && <p className="text-sm text-red-300" data-testid="checklist-error">{errorMsg}</p>}
      <button
        type="submit"
        disabled={status === "sending"}
        data-testid="checklist-submit"
        data-cursor="org"
        className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#D4AF37] px-8 py-4 text-sm font-bold text-[#0A2E2C] transition-colors hover:bg-[#B99628] disabled:opacity-60"
      >
        {status === "sending" ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : <><Mail className="h-4 w-4" /> Email me the checklist</>}
      </button>
      <p className="text-center font-mono text-[0.58rem] uppercase tracking-[0.16em] text-[#F8FAFC]/40">No spam. Just the checklist, straight to your inbox.</p>
    </form>
  );
};

export default function Corporate() {
  useCorporateSeo();

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-[#0A2E2C] text-[#F8FAFC]" data-testid="corporate-page">
      <div className="grain-overlay" aria-hidden="true" />

      {/* HERO */}
      <section className="relative flex min-h-[88svh] items-center px-6 py-24 sm:px-10 lg:px-12" data-testid="corporate-hero">
        <div className="absolute inset-0" style={{ background: "radial-gradient(1000px 500px at 15% 20%, rgba(174,230,213,0.08), transparent 60%), radial-gradient(800px 420px at 85% 80%, rgba(212,175,55,0.07), transparent 60%)" }} />
        <div className="relative z-10 mx-auto w-full max-w-5xl">
          <motion.p className="font-mono text-[0.7rem] uppercase tracking-[0.32em] text-[#D4AF37]" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.8 }}>
            Corporate wellness · Forte building · Employee wellbeing
          </motion.p>
          <motion.h1 className="mt-6 max-w-3xl font-serif text-4xl font-light leading-[1.04] tracking-tight sm:text-5xl lg:text-6xl" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 1 }}>
            Your team doesn&#39;t need another webinar. It needs its <span className="italic text-[#D4AF37]">rhythm back.</span>
          </motion.h1>
          <motion.p className="mt-7 max-w-2xl text-base leading-relaxed text-[#F8FAFC]/85 sm:text-lg" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 1 }}>
            Casey Muze brings science-backed, bilateral-rhythm keynotes and forte-building workshops to corporate
            teams — turning silent stress and siloed departments into one synchronized, re-energized team.
          </motion.p>
          <motion.div className="mt-11 flex w-full flex-col items-stretch gap-3 sm:w-auto sm:flex-row sm:items-center sm:gap-4" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.9 }}>
            <a href="/#booking" data-testid="corporate-book-cta" data-cursor="org" className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-8 py-4 text-sm font-bold text-[#0A2E2C] shadow-lg shadow-[#0A2E2C]/40 transition-all hover:scale-[1.03] hover:bg-[#B99628] sm:w-auto">
              Book Casey for Your Team
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </a>
            <a href="#checklist" data-testid="corporate-checklist-cta" data-cursor="rhythm" className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#AEE6D5]/30 bg-white/10 px-6 py-4 text-sm text-[#F8FAFC] backdrop-blur-xl transition-colors hover:bg-white/20 sm:w-auto">
              <ArrowDown className="h-4 w-4 text-[#AEE6D5]" />
              Get the Burnout Reset Checklist
            </a>
          </motion.div>
        </div>
      </section>

      {/* PAINS */}
      <section className="relative z-10 bg-[#0B3B3C] px-6 py-20 sm:px-10 lg:px-12 lg:py-28" data-testid="corporate-pains">
        <div className="mx-auto max-w-6xl">
          <div className="mb-14 max-w-3xl">
            <p className="font-mono text-[0.7rem] uppercase tracking-[0.3em] text-[#D4AF37]">The real problem</p>
            <h2 className="mt-4 font-serif text-4xl font-light leading-[1.05] tracking-tight sm:text-5xl">
              Burnout isn&#39;t a people problem. It&#39;s a <span className="italic text-[#AEE6D5]">rhythm problem.</span>
            </h2>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {PAINS.map((p, i) => (
              <motion.div key={p.testid} data-testid={p.testid} className="rounded-3xl border border-[#AEE6D5]/15 bg-[#0A2E2C]/60 p-8 transition-colors hover:border-[#D4AF37]/40" initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.7, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}>
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#D4AF37]/12 text-[#D4AF37]"><p.Icon className="h-6 w-6" strokeWidth={1.6} /></span>
                <h3 className="mt-6 font-serif text-2xl font-light leading-tight tracking-tight">{p.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[#F8FAFC]/75">{p.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FORMATS */}
      <section className="relative z-10 bg-[#0A2E2C] px-6 py-20 sm:px-10 lg:px-12 lg:py-28" data-testid="corporate-formats">
        <div className="mx-auto max-w-6xl">
          <div className="mb-14 max-w-3xl">
            <p className="font-mono text-[0.7rem] uppercase tracking-[0.3em] text-[#D4AF37]">What Casey delivers</p>
            <h2 className="mt-4 font-serif text-4xl font-light leading-[1.05] tracking-tight sm:text-5xl">
              Three ways to bring your team back <span className="italic text-[#AEE6D5]">into sync.</span>
            </h2>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {FORMATS.map((f, i) => (
              <motion.div key={f.testid} data-testid={f.testid} className="flex flex-col rounded-3xl border border-[#AEE6D5]/15 bg-[#0B3B3C]/70 p-8 transition-colors hover:border-[#D4AF37]/40" initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.7, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}>
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#D4AF37]/12 text-[#D4AF37]"><f.Icon className="h-6 w-6" strokeWidth={1.6} /></span>
                <h3 className="mt-6 font-serif text-2xl font-light leading-tight tracking-tight">{f.title}</h3>
                <p className="mt-1.5 font-mono text-[0.6rem] uppercase tracking-[0.16em] text-[#AEE6D5]">{f.meta}</p>
                <p className="mt-4 flex-1 text-sm leading-relaxed text-[#F8FAFC]/75">{f.body}</p>
                <a href="/#booking" data-testid={`${f.testid}-cta`} data-cursor="org" className="group mt-6 inline-flex items-center gap-2 self-start rounded-full border border-[#D4AF37]/50 px-5 py-2.5 text-xs font-bold uppercase tracking-[0.12em] text-[#D4AF37] transition-colors hover:bg-[#D4AF37] hover:text-[#0A2E2C]">
                  Check availability
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PROOF */}
      <section className="relative z-10 bg-[#0B3B3C] px-6 py-20 sm:px-10 lg:px-12 lg:py-28" data-testid="corporate-proof">
        <div className="mx-auto max-w-3xl text-center">
          <Quote className="mx-auto h-9 w-9 text-[#D4AF37]/70" strokeWidth={1.4} />
          <div className="mt-5 flex justify-center gap-1" aria-hidden="true">
            {[0, 1, 2, 3, 4].map((s) => <Star key={s} className="h-4 w-4 fill-[#D4AF37] text-[#D4AF37]" />)}
          </div>
          <blockquote className="mt-6 font-serif text-2xl font-light leading-snug text-[#F8FAFC] sm:text-3xl">
            “Our team came in stressed and siloed. They left in sync — laughing, collaborating, and fully present.
            Best culture investment we&#39;ve made this year.”
          </blockquote>
          <p className="mt-6 font-mono text-[0.62rem] uppercase tracking-[0.2em] text-[#AEE6D5]">People &amp; Culture Lead · Corporate Team</p>
        </div>
      </section>

      {/* LEAD MAGNET */}
      <section id="checklist" className="relative z-10 scroll-mt-8 bg-[#0A2E2C] px-6 py-20 sm:px-10 lg:px-12 lg:py-28" data-testid="corporate-leadmagnet">
        <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-2">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.8 }}>
            <p className="font-mono text-[0.7rem] uppercase tracking-[0.3em] text-[#D4AF37]">Free resource</p>
            <h2 className="mt-4 font-serif text-4xl font-light leading-tight tracking-tight sm:text-5xl">
              The Team Burnout <span className="italic text-[#AEE6D5]">Reset Checklist.</span>
            </h2>
            <p className="mt-5 max-w-md text-sm leading-relaxed text-[#F8FAFC]/75">
              Twelve small, practical moves to bring a stressed team back into rhythm — from sixty-second downbeats
              that open meetings to the shutdown ritual that lets people actually rest. A taste of what Casey
              brings to the stage, delivered straight to your inbox.
            </p>
            <ul className="mt-6 space-y-2.5">
              {["Name the static — make burnout speakable", "Open every meeting with a downbeat", "End the day with a clear cadence"].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-sm text-[#F8FAFC]/80">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#D4AF37]" /> {t}
                </li>
              ))}
              <li className="flex items-start gap-2.5 text-sm italic text-[#AEE6D5]/70">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#D4AF37]" /> …plus nine more inside
              </li>
            </ul>
          </motion.div>
          <motion.div className="rounded-3xl border border-[#AEE6D5]/20 bg-[#0B3B3C]/60 p-8 sm:p-10" initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.8 }}>
            <ChecklistCapture />
          </motion.div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative z-10 bg-[#0B3B3C] px-6 py-20 text-center sm:px-10 lg:py-28" data-testid="corporate-final-cta">
        <div className="mx-auto max-w-3xl">
          <p className="font-mono text-[0.7rem] uppercase tracking-[0.3em] text-[#D4AF37]">Ready when you are</p>
          <h2 className="mt-4 font-serif text-4xl font-light leading-[1.05] tracking-tight sm:text-5xl">
            Bring the reset to your team <span className="italic text-[#AEE6D5]">live.</span>
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-sm leading-relaxed text-[#F8FAFC]/70">
            Tell us about your event, your people, and your goals — Casey&#39;s team will follow up with
            availability, formats, and rates.
          </p>
          <a href="/#booking" data-testid="corporate-final-book-cta" data-cursor="org" className="group mt-9 inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-8 py-4 text-sm font-bold text-[#0A2E2C] shadow-lg shadow-[#0A2E2C]/40 transition-all hover:scale-[1.03] hover:bg-[#B99628]">
            Check Speaking Availability &amp; Rates
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </a>
        </div>
      </section>

      <CadenceFooter />
      <ChatWidget />
    </div>
  );
}
